
// OrderSuccessPage.js - Salesperson with "Add New" at Top

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  lookupCustomer,
  addCustomer,
  updateCustomer,
  resetCustomer,
  clearAddState,
  clearUpdateState,
} from "../../services/features/customers/customerSlice";
import {
  fetchSalespersons,
  addSalesperson,
  clearSalespersonErrors,
} from "../../services/features/salesperson/salespersonSlice";
import { fetchProducts } from "../../services/features/products/productSlice";
import API from "../../services/API/api";
import { useTheme } from "../../context/ThemeContext";
import "./OrderSuccessPage.css";

import {
  FileText,
  ChevronDown,
  X,
  User,
  Truck,
  Calendar,
  Phone,
  UserPlus,
  CheckCircle,
  MapPin,
  Building2,
  AlertTriangle,
  WifiOff,
  CreditCard,
  IndianRupee,
  Check,
  Plus,
  Loader,
} from "lucide-react";

const PAYMENT_MODES = [
  { id: 1, label: "Cash" },
  { id: 2, label: "GPay" },
  { id: 3, label: "Credit Card" },
  { id: 4, label: "Debit Card" },
  { id: 5, label: "Office Use" },
  { id: 6, label: "Loan Amount" },
];

const ORDER_TYPES = [
  { id: 1, label: "OEM" },
  { id: 2, label: "TOOLS" },
];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// ─── Safe numeric parsing (preserves 0, handles "", null, NaN, negatives) ───
const parseAmount = (val) => {
  if (val === "" || val === null || val === undefined) return 0;
  const num = parseFloat(val);
  if (isNaN(num) || num < 0) return 0;
  return num;
};

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const {
    cartItems,
    paymentMode: initialPaymentMode,
    date,
    batchSelections = {},
    showBatchSelector = false,
    priceType = "retailerPrice",
    hasDefaultBatches = false,
  } = location.state || {};

  // ─── Redux ──────────────────────────────────────────────────────────────
  const {
    lookupData: customer,
    lookupState,
    addLoading,
    addSuccess,
    updateLoading,
    updateSuccess,
    error: customerError,
  } = useSelector((s) => s.customer);
  const user = useSelector((state) => state.auth.user);

  const {
    list: salespersons,
    loading: salespersonsLoading,
    adding: addingSalesperson,
    addError: salespersonAddError,
  } = useSelector((state) => state.salesperson);

  // ─── State ──────────────────────────────────────────────────────────────
  const [referenceNo, setReferenceNo] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editShopName, setEditShopName] = useState("");
  const [editCustomerType, setEditCustomerType] = useState("customer");
  const [sameAsBuyer, setSameAsBuyer] = useState(true);
  const [shipToName, setShipToName] = useState("");
  const [shipToPhone, setShipToPhone] = useState("");
  const [shipToAddress, setShipToAddress] = useState("");
  const [shipToCity, setShipToCity] = useState("");
  const [shipToState, setShipToState] = useState("");
  const [courierCharge, setCourierCharge] = useState("0");
  const [gstAmount, setGstAmount] = useState("0");
  const [selectedSP, setSelectedSP] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [discount, setDiscount] = useState("0");
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(
    initialPaymentMode ? { label: initialPaymentMode } : null,
  );
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [customerType, setCustomerType] = useState("customer");
  const [shopName, setShopName] = useState("");
  const [orderType, setOrderType] = useState("");
  const [orderTypeDropdownOpen, setOrderTypeDropdownOpen] = useState(false);

  // ─── Add Salesperson Modal ─────────────────────────────────────────────
  const [addSalespersonModalVisible, setAddSalespersonModalVisible] = useState(false);
  const [newSalespersonName, setNewSalespersonName] = useState("");
  const [salespersonAddErrorMsg, setSalespersonAddErrorMsg] = useState("");

  // Refs
  const paymentDropdownRef = useRef();
  const salespersonDropdownRef = useRef();
  const orderTypeDropdownRef = useRef();

  // ─── Load salespersons on mount ────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchSalespersons());
  }, [dispatch]);

  // ─── Reset salesperson error when modal closes ────────────────────────
  useEffect(() => {
    if (!addSalespersonModalVisible) {
      setSalespersonAddErrorMsg("");
      dispatch(clearSalespersonErrors());
    }
  }, [addSalespersonModalVisible, dispatch]);

  useEffect(() => {
    if (salespersonAddError) {
      setSalespersonAddErrorMsg(salespersonAddError);
    }
  }, [salespersonAddError]);

  // ─── Customer effects ──────────────────────────────────────────────────
  useEffect(() => {
    if (!cartItems) navigate("/order-cart");
  }, [cartItems, navigate]);

  useEffect(() => {
    return () => dispatch(resetCustomer());
  }, [dispatch]);

  useEffect(() => {
    if (buyerPhone.length === 10) {
      setPhoneError(false);
      dispatch(lookupCustomer(buyerPhone));
    } else {
      dispatch(resetCustomer());
    }
  }, [buyerPhone, dispatch]);

  useEffect(() => {
    if (addSuccess) {
      setAddModalVisible(false);
      setNewName("");
      setNewAddress("");
      setNewCity("");
      setNewState("");
      dispatch(clearAddState());
      dispatch(lookupCustomer(buyerPhone));
    }
    if (customerError && addLoading === false) {
      alert("Error: " + customerError);
    }
  }, [addSuccess, customerError, addLoading, dispatch, buyerPhone]);

  useEffect(() => {
    if (updateSuccess) {
      setEditModalVisible(false);
      dispatch(lookupCustomer(buyerPhone));
      dispatch(clearUpdateState());
      alert("Customer details updated successfully.");
    }
    if (customerError && updateLoading === false && updateSuccess === false) {
      alert("Error: " + customerError);
    }
  }, [updateSuccess, customerError, updateLoading, dispatch, buyerPhone]);

  // ─── Close dropdowns ──────────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setPaymentDropdownOpen(false);
      }
      if (
        salespersonDropdownRef.current &&
        !salespersonDropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
      if (
        orderTypeDropdownRef.current &&
        !orderTypeDropdownRef.current.contains(event.target)
      ) {
        setOrderTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── Add Salesperson Handler ──────────────────────────────────────────
  const handleAddSalesperson = async () => {
    const trimmedName = newSalespersonName.trim();
    if (!trimmedName) {
      setSalespersonAddErrorMsg("Please enter a salesperson name.");
      return;
    }

    setSalespersonAddErrorMsg("");

    try {
      const result = await dispatch(addSalesperson(trimmedName)).unwrap();

      setSelectedSP(result);
      setNewSalespersonName("");
      setAddSalespersonModalVisible(false);
      setDropdownOpen(false);

      await dispatch(fetchSalespersons());
    } catch (err) {
      setSalespersonAddErrorMsg(err || "Failed to add salesperson. Please try again.");
    }
  };

  // ─── Get batch allocations ────────────────────────────────────────────
  const getBatchAllocationsForItem = useCallback(
    (item) => {
      let allocations = [];

      if (batchSelections && batchSelections[item.id]?.batchAllocations) {
        allocations = batchSelections[item.id].batchAllocations;
      }

      if (allocations.length === 0 && item.batchAllocations) {
        allocations = item.batchAllocations;
      }

      return allocations;
    },
    [batchSelections],
  );

  // ─── goToInvoice ──────────────────────────────────────────────────────
  const goToInvoice = async () => {
    if (!selectedSP) {
      alert("Please select a Salesperson before proceeding.");
      setConfirmVisible(false);
      return;
    }

    setIsConfirming(true);
    setConfirmVisible(false);

    try {
      const finalShipToName = sameAsBuyer
        ? customer?.name
        : shipToName;
      const finalShipToPhone = sameAsBuyer ? buyerPhone : shipToPhone;
      const finalShipToAddress = sameAsBuyer
        ? customer?.address || ""
        : shipToAddress;
      const finalShipToCity = sameAsBuyer ? customer?.city || "" : shipToCity;
      const finalShipToState = sameAsBuyer
        ? customer?.state || ""
        : shipToState;

      const invoiceItems = cartItems.map((item) => {
        const allocations = getBatchAllocationsForItem(item);
        const hasValidBatches =
          allocations &&
          allocations.length > 0 &&
          allocations.some(
            (a) => a.batchNumber && a.batchNumber !== "default",
          );

        const baseItem = {
          productId: item.id,
          name: item.name,
          qty: item.qty || 0,
          price: item.price || item.originalPrice || 0,
          useDefaultPrice: !hasValidBatches,
        };

        if (hasValidBatches) {
          baseItem.batchAllocations = allocations
            .filter((a) => a.batchNumber && a.batchNumber !== "default")
            .map((alloc) => ({
              batchNumber: alloc.batchNumber,
              qty: alloc.qty || item.qty || 0,
              purchaseCost: alloc.purchaseCost || item.price || 0,
              sellingPrice: alloc.sellingPrice || item.price || 0,
            }));
        }

        return baseItem;
      });

      // ─── SAFE NUMERIC PARSING ──────────────────────────────────────────
      const subtotal = cartItems.reduce(
        (sum, item) =>
          sum + (item.price || item.originalPrice || 0) * (item.qty || 0),
        0,
      );
      const discountAmount = parseAmount(discount);
      const afterDiscount = Math.max(subtotal - discountAmount, 0);
      const courier = parseAmount(courierCharge);
      const gst = parseAmount(gstAmount);
      const totalWithCourier = afterDiscount + courier + gst;

      const payload = {
        billerName: user?.name || "Unknown",
        items: invoiceItems,
        totalAmount: totalWithCourier,
        paymentMode: selectedPaymentMode?.label || initialPaymentMode || "Cash",
        status: "completed",
        customerPhone: buyerPhone,
        customerName: customer?.name || "Guest",
        customerType: customer?.type || "customer",
        shopName: customer?.shopName || "",
        customerAddress: customer?.address || "",
        customerCity: customer?.city || "",
        customerState: customer?.state || "",
        sameAsBuyer,
        shippingAddress: {
          name: finalShipToName || customer?.name || "Guest",
          phone: finalShipToPhone || buyerPhone || "",
          address: finalShipToAddress || customer?.address || "",
          city: finalShipToCity || customer?.city || "",
          state: finalShipToState || customer?.state || "",
        },
        subtotal,
        discount: discountAmount,     // number
        courierCharge: courier,       // number (0 allowed)
        gstAmount: gst,               // number (0 allowed)
        salesperson: selectedSP?.name || "",
        referenceNo: referenceNo || "",
        invoiceDate: date || new Date().toISOString(),
        orderType: orderType || "",
        priceType: priceType || "retailerPrice",
        allowDefaultBatches: true,
        hasDefaultBatches:
          hasDefaultBatches ||
          invoiceItems.some((item) => item.useDefaultPrice),
      };

      const invoiceRes = await API.post("/api/invoices", payload);
      const invoiceNumber = invoiceRes.data.invoice.invoiceNumber;

      await dispatch(fetchProducts());

      navigate("/invoice", {
        state: {
          invoiceNumber,
          items: cartItems,
          total: totalWithCourier,
          paymentMode: selectedPaymentMode?.label || initialPaymentMode,
          date: date,
          buyerName: customer?.name || "—",
          buyerPhone,
          buyerAddress: customer?.address || "",
          buyerCity: customer?.city || "",
          buyerState: customer?.state || "",
          courierCharge: courier,       // number, 0 allowed
          discount: discountAmount,     // number
          gstAmount: gst,               // number
          salesperson: selectedSP?.name || "",
          referenceNo,
          shipToName: finalShipToName,
          shipToPhone: finalShipToPhone,
          shipToAddress: finalShipToAddress,
          shipToCity: finalShipToCity,
          shipToState: finalShipToState,
          customerType: customer?.type,
          shopName: customer?.shopName,
          orderType: orderType || "",
          batchSelections: batchSelections,
          showBatchSelector: showBatchSelector,
          priceType: priceType,
        },
      });
    } catch (err) {
      console.error("❌ Invoice creation error:", err);

      let errorMessage = "Failed to confirm order. Please try again.";

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
        if (err.response.data.code === "INSUFFICIENT_STOCK") {
          errorMessage = `Insufficient stock: ${err.response.data.message}`;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      alert(errorMessage);
    } finally {
      setIsConfirming(false);
    }
  };

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleViewInvoice = () => {
    if (!selectedSP) {
      alert("Please select a Salesperson before proceeding.");
      return;
    }

    if (!referenceNo.trim()) {
      alert("Please enter a Reference Number before proceeding.");
      return;
    }

    if (!orderType) {
      alert("Please select an Order Type before proceeding.");
      return;
    }

    if (buyerPhone.length < 10) {
      setPhoneError(true);
      alert("Please enter a valid 10-digit phone number.");
      return;
    }
    if (lookupState === "loading") {
      alert("Verifying customer details...");
      return;
    }
    if (lookupState === "notfound") {
      setPhoneError(true);
      if (
        window.confirm(
          "Customer not found. Would you like to add this customer?",
        )
      ) {
        setAddModalVisible(true);
      }
      return;
    }
    if (lookupState === "error") {
      alert("Could not reach server. Check your connection.");
      return;
    }
    if (lookupState === "found" && customer) {
      setPhoneError(false);
      setConfirmVisible(true);
    }
  };

  const handleSaveNewCustomer = () => {
    if (!newName.trim()) {
      alert("Customer name is required.");
      return;
    }
    if (customerType === "shop" && !shopName.trim()) {
      alert("Shop name is required.");
      return;
    }
    dispatch(
      addCustomer({
        phone: buyerPhone,
        name: newName.trim(),
        address: newAddress.trim(),
        city: newCity.trim(),
        state: newState.trim(),
        type: customerType,
        shopName: shopName.trim(),
      }),
    );
  };

  // ─── Derived totals for preview ───────────────────────────────────────
  const discountAmount = parseAmount(discount);
  const subtotal = cartItems
    ? cartItems.reduce(
        (sum, item) =>
          sum + (item.price || item.originalPrice || 0) * (item.qty || 0),
        0,
      )
    : 0;
  const afterDiscount = Math.max(subtotal - discountAmount, 0);
  const courier = parseAmount(courierCharge);
  const gst = parseAmount(gstAmount);
  const grandTotalWithCourier = afterDiscount + courier + gst;

  if (!cartItems)
    return <div className="loading-state">No cart items found</div>;

  return (
    <div className={`order-success-page ${isDark ? "dark" : ""}`}>
      <div className="order-success-container">
        <div className="success-header">📄 Order Confirm</div>

        {/* Batch Summary */}
        {showBatchSelector && (
          <div className="batch-summary-card">
            <h4>Batch Allocations</h4>
            {cartItems.map((item) => {
              const allocations = getBatchAllocationsForItem(item);
              const hasValidBatches =
                allocations &&
                allocations.length > 0 &&
                allocations.some(
                  (a) => a.batchNumber && a.batchNumber !== "default",
                );

              return (
                <div key={item.id} className="batch-summary-item">
                  <div className="batch-summary-product">{item.name}</div>
                  <div className="batch-summary-batches">
                    {hasValidBatches ? (
                      allocations
                        .filter(
                          (a) => a.batchNumber && a.batchNumber !== "default",
                        )
                        .map((alloc, idx) => (
                          <span key={idx} className="batch-summary-tag">
                            {alloc.batchNumber}: {alloc.qty || item.qty} units
                          </span>
                        ))
                    ) : (
                      <span className="batch-summary-tag default-batch">
                        Using default price (no batch)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="form-card">
          <h3>Delivery & Invoice Details</h3>

          {/* Reference No */}
          <div className="field-group">
            <label>
              <FileText size={14} /> Reference No. *
            </label>
            <input
              type="text"
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              placeholder="e.g. PO-12345 (Required)"
              className="field-input"
              required
            />
            {!referenceNo.trim() && (
              <div className="error-text">Reference Number is required</div>
            )}
          </div>

          {/* Order Type */}
          <div className="field-group" ref={orderTypeDropdownRef}>
            <label>
              <FileText size={14} /> Order Type *
            </label>
            <div
              className="custom-select"
              onClick={() => setOrderTypeDropdownOpen(!orderTypeDropdownOpen)}
            >
              <span>{orderType ? orderType : "Select order type…"}</span>
              <ChevronDown
                size={16}
                className={`dropdown-arrow ${orderTypeDropdownOpen ? "open" : ""}`}
              />
            </div>
            {orderTypeDropdownOpen && (
              <div className="dropdown-list">
                {ORDER_TYPES.map((type) => (
                  <div
                    key={type.id}
                    className={`dropdown-item ${orderType === type.label ? "active" : ""}`}
                    onClick={() => {
                      setOrderType(type.label);
                      setOrderTypeDropdownOpen(false);
                    }}
                  >
                    {type.label}
                  </div>
                ))}
              </div>
            )}
            {!orderType && (
              <div className="error-text">Order Type is required</div>
            )}
          </div>

          {/* Payment Mode */}
          <div className="field-group" ref={paymentDropdownRef}>
            <label>
              <CreditCard size={14} /> Payment Mode
            </label>
            <div
              className="custom-select"
              onClick={() => setPaymentDropdownOpen(!paymentDropdownOpen)}
            >
              <span>
                {selectedPaymentMode
                  ? selectedPaymentMode.label
                  : "Select payment mode…"}
              </span>
              <ChevronDown
                size={16}
                className={`dropdown-arrow ${paymentDropdownOpen ? "open" : ""}`}
              />
            </div>
            {paymentDropdownOpen && (
              <div className="dropdown-list">
                {PAYMENT_MODES.map((mode) => (
                  <div
                    key={mode.id}
                    className={`dropdown-item ${selectedPaymentMode?.id === mode.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedPaymentMode(mode);
                      setPaymentDropdownOpen(false);
                    }}
                  >
                    {mode.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Subtotal */}
          <div className="field-group">
            <label>
              <IndianRupee size={14} /> Subtotal
            </label>
            <div className="readonly-field">
              ₹{subtotal.toLocaleString("en-IN")}
            </div>
          </div>

          {/* Discount */}
          <div className="field-group">
            <label>
              <IndianRupee size={14} /> Discount
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              onBlur={(e) => {
                const num = parseFloat(e.target.value);
                setDiscount(
                  isNaN(num) || num < 0 ? "0" : String(num),
                );
              }}
              className="field-input"
              placeholder="0"
            />
          </div>

          {/* Invoice Date */}
          <div className="field-group">
            <label>
              <Calendar size={14} /> Invoice Date
            </label>
            <div className="readonly-field">
              {formatDate(date || new Date().toISOString())}
            </div>
          </div>

          {/* Salesperson */}
          <div className="field-group" ref={salespersonDropdownRef}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "var(--text-secondary)",
                  margin: 0,
                }}
              >
                <User size={14} /> Salesperson *
              </label>
              <button
                type="button"
                onClick={() => setAddSalespersonModalVisible(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  background: "var(--red)",
                  color: "#fff",
                  border: "none",
                  padding: "4px 12px",
                  borderRadius: "4px",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--red-bright)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--red)";
                }}
              >
                <Plus size={14} /> Add New
              </button>
            </div>

            <div
              className={`custom-select ${!selectedSP ? "error" : ""}`}
              onClick={() =>
                !salespersonsLoading && setDropdownOpen(!dropdownOpen)
              }
              style={{
                borderColor: !selectedSP ? "var(--red-bright)" : undefined,
                cursor: salespersonsLoading ? "default" : "pointer",
              }}
            >
              <span
                style={{
                  color: !selectedSP ? "var(--text-muted)" : undefined,
                }}
              >
                {salespersonsLoading ? (
                  <Loader
                    size={16}
                    className="spin"
                    style={{ display: "inline-block", marginRight: "6px" }}
                  />
                ) : null}
                {selectedSP
                  ? selectedSP.name
                  : "Select salesperson… (Required)"}
              </span>
              <ChevronDown
                size={16}
                className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
              />
            </div>
            {!selectedSP && !salespersonsLoading && (
              <div className="error-text">Salesperson is required</div>
            )}
            {salespersonsLoading && (
              <div className="status-row">Loading salespersons...</div>
            )}
            {dropdownOpen && !salespersonsLoading && (
              <div
                className="dropdown-list"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {salespersons.length === 0 ? (
                  <div
                    style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      color: "var(--text-muted)",
                    }}
                  >
                    No salespersons found. Click "Add New" to create one.
                  </div>
                ) : (
                  salespersons.map((sp) => (
                    <div
                      key={sp.id}
                      className={`dropdown-item ${selectedSP?.id === sp.id ? "active" : ""}`}
                      onClick={() => {
                        setSelectedSP(sp);
                        setDropdownOpen(false);
                      }}
                    >
                      {sp.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Phone & Customer Lookup */}
          <div className="field-group">
            <label>
              <Phone size={14} /> Phone Number *
            </label>
            <input
              type="tel"
              maxLength="10"
              value={buyerPhone}
              onChange={(e) => setBuyerPhone(e.target.value)}
              className={`field-input ${phoneError ? "error" : ""}`}
              placeholder="Enter 10-digit mobile number"
            />
            {phoneError && buyerPhone.length < 10 && (
              <div className="error-text">
                Enter a valid 10-digit phone number
              </div>
            )}
            {lookupState === "loading" && (
              <div className="status-row">Checking customer database...</div>
            )}
            {lookupState === "found" && customer && (
              <div className="found-card">
                <div className="found-header">
                  <CheckCircle size={15} /> Customer Found
                  <button
                    className="edit-btn"
                    onClick={() => {
                      setEditName(customer.name || "");
                      setEditAddress(customer.address || "");
                      setEditCity(customer.city || "");
                      setEditState(customer.state || "");
                      setEditShopName(customer.shopName || "");
                      setEditCustomerType(customer.type || "customer");
                      setEditModalVisible(true);
                    }}
                  >
                    Edit
                  </button>
                </div>
                <div className="customer-name">
                  {customer.type === "shop"
                    ? `${customer.name} (${customer.shopName})`
                    : customer.name}
                </div>
                {customer.address && (
                  <div className="customer-sub address-line">
                    <MapPin size={14} />
                    <span>{customer.address}</span>
                  </div>
                )}
                {(customer.city || customer.state) && (
                  <div className="customer-sub">
                    <Building2 size={14} />{" "}
                    {[customer.city, customer.state].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
            )}
            {lookupState === "notfound" && (
              <div className="notfound-box">
                <AlertTriangle size={13} /> No customer found.
                <button
                  className="add-btn"
                  onClick={() => setAddModalVisible(true)}
                >
                  + Add Customer
                </button>
              </div>
            )}
            {lookupState === "error" && (
              <div className="notfound-box">
                <WifiOff size={13} /> Could not reach server.
                <button
                  className="add-btn"
                  onClick={() => dispatch(lookupCustomer(buyerPhone))}
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Shipping Address */}
          <div className="field-group">
            <label>
              <Truck size={14} /> Shipping Address
            </label>
            <div className="switch-row">
              <span>Same as buyer address</span>
              <button
                className={`switch ${sameAsBuyer ? "active" : ""}`}
                onClick={() => setSameAsBuyer(!sameAsBuyer)}
              >
                {sameAsBuyer ? "ON" : "OFF"}
              </button>
            </div>
            {!sameAsBuyer && (
              <>
                <input
                  type="text"
                  placeholder="Ship to Name"
                  value={shipToName}
                  onChange={(e) => setShipToName(e.target.value)}
                  className="field-input"
                />
                <input
                  type="tel"
                  placeholder="Ship to Phone"
                  value={shipToPhone}
                  onChange={(e) => setShipToPhone(e.target.value)}
                  className="field-input"
                />
                <textarea
                  placeholder="Ship to Address"
                  value={shipToAddress}
                  onChange={(e) => setShipToAddress(e.target.value)}
                  rows={2}
                  className="field-input"
                />
                <div className="row-inputs">
                  <input
                    type="text"
                    placeholder="City"
                    value={shipToCity}
                    onChange={(e) => setShipToCity(e.target.value)}
                    className="field-input"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={shipToState}
                    onChange={(e) => setShipToState(e.target.value)}
                    className="field-input"
                  />
                </div>
              </>
            )}
          </div>

          {/* GST Amount */}
          <div className="field-group">
            <label>
              <IndianRupee size={14} /> GST (₹)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={gstAmount}
              onChange={(e) => setGstAmount(e.target.value)}
              onBlur={(e) => {
                const num = parseFloat(e.target.value);
                setGstAmount(
                  isNaN(num) || num < 0 ? "0" : String(num),
                );
              }}
              className="field-input"
              placeholder="0"
            />
          </div>

          {/* Courier Charge */}
          <div className="field-group">
            <label>
              <Truck size={14} /> Courier Charge (₹)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={courierCharge}
              onChange={(e) => setCourierCharge(e.target.value)}
              onBlur={(e) => {
                const num = parseFloat(e.target.value);
                setCourierCharge(
                  isNaN(num) || num < 0 ? "0" : String(num),
                );
              }}
              className="field-input"
              placeholder="0"
            />
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--text-muted)",
                marginTop: "4px",
              }}
            >
              Enter 0 for no courier charge
            </div>
          </div>

          {/* Total Preview */}
          <div className="total-preview">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            {discountAmount > 0 && (
              <div className="summary-row">
                <span>Discount</span>
                <span className="discount">
                  - ₹{discountAmount.toLocaleString("en-IN")}
                </span>
              </div>
            )}
            <div className="summary-row">
              <span>Courier Charge</span>
              <span>₹{courier.toLocaleString("en-IN")}</span>
            </div>
            {gst > 0 && (
              <div className="summary-row">
                <span>GST</span>
                <span>₹{gst.toLocaleString("en-IN")}</span>
              </div>
            )}
            <hr />
            <div className="summary-row total">
              <span>Grand Total</span>
              <span>₹{grandTotalWithCourier.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>

        <button
          className="primary-btn"
          onClick={handleViewInvoice}
          disabled={
            isConfirming ||
            !referenceNo.trim() ||
            !orderType ||
            !selectedSP ||
            salespersonsLoading
          }
          style={{
            opacity:
              !referenceNo.trim() ||
              !orderType ||
              !selectedSP ||
              salespersonsLoading
                ? "0.5"
                : "1",
          }}
        >
          <FileText size={18} /> View Invoice
        </button>
      </div>

      {/* ─── Add Salesperson Modal ─── */}
      {addSalespersonModalVisible && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!addingSalesperson) {
              setAddSalespersonModalVisible(false);
              setSalespersonAddErrorMsg("");
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <UserPlus size={18} /> Add New Salesperson
              <button
                className="close-btn"
                onClick={() => {
                  if (!addingSalesperson) {
                    setAddSalespersonModalVisible(false);
                    setSalespersonAddErrorMsg("");
                  }
                }}
                disabled={addingSalesperson}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div
                className="phone-pill"
                style={{ background: "var(--bg-surface)" }}
              >
                <User size={13} /> New Salesperson
              </div>
              <input
                type="text"
                placeholder="Enter salesperson name *"
                value={newSalespersonName}
                onChange={(e) => {
                  setNewSalespersonName(e.target.value);
                  if (salespersonAddErrorMsg) setSalespersonAddErrorMsg("");
                }}
                className="modal-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !addingSalesperson) {
                    handleAddSalesperson();
                  }
                }}
                autoFocus
                disabled={addingSalesperson}
              />
              {salespersonAddErrorMsg && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--red-bright)",
                    marginBottom: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ⚠️ {salespersonAddErrorMsg}
                </div>
              )}
              <div
                style={{
                  fontSize: "0.7rem",
                  color: "var(--text-muted)",
                  marginBottom: "0.75rem",
                }}
              >
                This will add the salesperson to the database for all future
                invoices.
              </div>
              <button
                className="save-btn"
                onClick={handleAddSalesperson}
                disabled={addingSalesperson}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                {addingSalesperson ? (
                  <>
                    <Loader size={16} className="spin" /> Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} /> Add Salesperson
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {addModalVisible && (
        <div
          className="modal-overlay"
          onClick={() => setAddModalVisible(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <UserPlus size={18} /> Add New Customer
              <button
                className="close-btn"
                onClick={() => setAddModalVisible(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="phone-pill">
                <Phone size={13} /> {buyerPhone}
              </div>
              <div className="row-buttons">
                <button
                  className={`type-btn ${customerType === "customer" ? "active" : ""}`}
                  onClick={() => setCustomerType("customer")}
                >
                  Customer
                </button>
                <button
                  className={`type-btn ${customerType === "shop" ? "active" : ""}`}
                  onClick={() => setCustomerType("shop")}
                >
                  Shop
                </button>
              </div>
              <input
                type="text"
                placeholder="Customer Name *"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="modal-input"
              />
              {customerType === "shop" && (
                <input
                  type="text"
                  placeholder="Shop Name *"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  className="modal-input"
                />
              )}
              <label className="field-label">Delivery Address</label>
              <textarea
                placeholder="Street, landmark, area…"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                rows={3}
                className="modal-input address-textarea"
              />
              <div className="row-inputs">
                <input
                  type="text"
                  placeholder="City"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  className="modal-input"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  className="modal-input"
                />
              </div>
              <button
                className="save-btn"
                onClick={handleSaveNewCustomer}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "Save Customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {editModalVisible && (
        <div
          className="modal-overlay"
          onClick={() => setEditModalVisible(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <UserPlus size={18} /> Edit Customer
              <button
                className="close-btn"
                onClick={() => setEditModalVisible(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="phone-pill">
                <Phone size={13} /> {buyerPhone}
              </div>
              <div className="row-buttons">
                <button
                  className={`type-btn ${editCustomerType === "customer" ? "active" : ""}`}
                  onClick={() => setEditCustomerType("customer")}
                >
                  Customer
                </button>
                <button
                  className={`type-btn ${editCustomerType === "shop" ? "active" : ""}`}
                  onClick={() => setEditCustomerType("shop")}
                >
                  Shop
                </button>
              </div>
              <input
                type="text"
                placeholder="Customer Name *"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="modal-input"
              />
              {editCustomerType === "shop" && (
                <input
                  type="text"
                  placeholder="Shop Name *"
                  value={editShopName}
                  onChange={(e) => setEditShopName(e.target.value)}
                  className="modal-input"
                />
              )}
              <label className="field-label">Delivery Address</label>
              <textarea
                placeholder="Street, landmark, area…"
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                rows={3}
                className="modal-input address-textarea"
              />
              <div className="row-inputs">
                <input
                  type="text"
                  placeholder="City"
                  value={editCity}
                  onChange={(e) => setEditCity(e.target.value)}
                  className="modal-input"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={editState}
                  onChange={(e) => setEditState(e.target.value)}
                  className="modal-input"
                />
              </div>
              <button
                className="save-btn"
                onClick={() => {
                  if (!editName.trim()) return alert("Customer name required");
                  dispatch(
                    updateCustomer({
                      phone: buyerPhone,
                      data: {
                        name: editName.trim(),
                        address: editAddress.trim(),
                        city: editCity.trim(),
                        state: editState.trim(),
                        type: editCustomerType,
                        shopName: editShopName.trim(),
                      },
                    }),
                  );
                }}
                disabled={updateLoading}
              >
                {updateLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmVisible && (
        <div className="modal-overlay" onClick={() => setConfirmVisible(false)}>
          <div
            className="modal-content confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                <Check size={20} /> Ready to Generate Invoice?
              </h3>
              <button
                className="close-btn"
                onClick={() => setConfirmVisible(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <p>Please confirm the order details before proceeding.</p>

              {showBatchSelector && (
                <div className="confirm-batch-summary">
                  <h4>Batch Allocations</h4>
                  {cartItems.map((item) => {
                    const allocations = getBatchAllocationsForItem(item);
                    const hasValidBatches =
                      allocations &&
                      allocations.length > 0 &&
                      allocations.some(
                        (a) => a.batchNumber && a.batchNumber !== "default",
                      );

                    return (
                      <div key={item.id} className="confirm-batch-item">
                        <span className="confirm-batch-product">
                          {item.name}
                        </span>
                        {hasValidBatches ? (
                          allocations
                            .filter(
                              (a) =>
                                a.batchNumber && a.batchNumber !== "default",
                            )
                            .map((alloc, idx) => (
                              <span key={idx} className="confirm-batch-tag">
                                {alloc.batchNumber}: {alloc.qty || item.qty}
                              </span>
                            ))
                        ) : (
                          <span className="confirm-batch-tag default-batch">
                            Using default price (no batch)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="confirm-info">
                <div>
                  <strong>Reference No:</strong> <span>{referenceNo}</span>
                </div>
                <div>
                  <strong>Order Type:</strong> <span>{orderType || "—"}</span>
                </div>
                <div>
                  <strong>Salesperson:</strong>{" "}
                  <span>{selectedSP?.name || "—"}</span>
                </div>
                <div>
                  <strong>Buyer:</strong>
                  <span>
                    {customer?.type === "shop"
                      ? `${customer?.name} (${customer?.shopName})`
                      : customer?.name || "—"}
                  </span>
                </div>
                <div>
                  <strong>Phone:</strong> <span>{buyerPhone}</span>
                </div>
                <div>
                  <strong>Address:</strong>{" "}
                  <span>{customer?.address || "—"}</span>
                </div>
                <div>
                  <strong>City/State:</strong>{" "}
                  <span>
                    {[customer?.city, customer?.state]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </span>
                </div>
                <div>
                  <strong>Courier:</strong> <span>₹{courier}</span>
                </div>
                {discountAmount > 0 && (
                  <div>
                    <strong>Discount:</strong> <span>₹{discountAmount}</span>
                  </div>
                )}
                {gst > 0 && (
                  <div>
                    <strong>GST:</strong> <span>₹{gst}</span>
                  </div>
                )}
                <div>
                  <strong>Grand Total:</strong>{" "}
                  <span>₹{grandTotalWithCourier.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <div className="confirm-buttons">
                <button
                  className="primary-btn"
                  onClick={goToInvoice}
                  disabled={
                    isConfirming || !referenceNo.trim() || !orderType || !selectedSP
                  }
                >
                  {isConfirming
                    ? "Processing..."
                    : "Confirm & Generate Invoice"}
                </button>
                <button
                  className="outline-btn"
                  onClick={() => setConfirmVisible(false)}
                >
                  Go Back & Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSuccessPage;

//---------------- 01.10.2026 ---------------------------
// // OrderSuccessPage.js - Salesperson with "Add New" at Top

// import React, { useState, useEffect, useRef, useCallback } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useLocation, useNavigate } from "react-router-dom";
// import {
//   lookupCustomer,
//   addCustomer,
//   updateCustomer,
//   resetCustomer,
//   clearAddState,
//   clearUpdateState,
// } from "../../services/features/customers/customerSlice";
// import {
//   fetchSalespersons,
//   addSalesperson,
//   clearSalespersonErrors,
// } from "../../services/features/salesperson/salespersonSlice";
// import { fetchProducts } from "../../services/features/products/productSlice";
// import API from "../../services/API/api";
// import { useTheme } from "../../context/ThemeContext";
// import "./OrderSuccessPage.css";

// import {
//   FileText,
//   ChevronDown,
//   X,
//   User,
//   Truck,
//   Calendar,
//   Phone,
//   UserPlus,
//   CheckCircle,
//   MapPin,
//   Building2,
//   AlertTriangle,
//   WifiOff,
//   CreditCard,
//   IndianRupee,
//   Check,
//   Plus,
//   Loader,
// } from "lucide-react";

// const PAYMENT_MODES = [
//   { id: 1, label: "Cash" },
//   { id: 2, label: "GPay" },
//   { id: 3, label: "Credit Card" },
//   { id: 4, label: "Debit Card" },
//   { id: 5, label: "Office Use" },
//   { id: 6, label: "Loan Amount" },
// ];

// const ORDER_TYPES = [
//   { id: 1, label: "OEM" },
//   { id: 2, label: "TOOLS" },
// ];

// const formatDate = (iso) =>
//   new Date(iso).toLocaleDateString("en-IN", {
//     day: "2-digit",
//     month: "short",
//     year: "numeric",
//   });

// const OrderSuccessPage = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const { theme } = useTheme();
//   const isDark = theme === "dark";

//   const {
//     cartItems,
//     paymentMode: initialPaymentMode,
//     date,
//     batchSelections = {},
//     showBatchSelector = false,
//     priceType = "retailerPrice",
//     hasDefaultBatches = false,
//   } = location.state || {};

//   // ─── Redux ──────────────────────────────────────────────────────────────
//   const {
//     lookupData: customer,
//     lookupState,
//     addLoading,
//     addSuccess,
//     updateLoading,
//     updateSuccess,
//     error: customerError,
//   } = useSelector((s) => s.customer);
//   const user = useSelector((state) => state.auth.user);
  
//   const {
//     list: salespersons,
//     loading: salespersonsLoading,
//     adding: addingSalesperson,
//     addError: salespersonAddError,
//   } = useSelector((state) => state.salesperson);

//   // ─── State ──────────────────────────────────────────────────────────────
//   const [referenceNo, setReferenceNo] = useState("");
//   const [buyerPhone, setBuyerPhone] = useState("");
//   const [phoneError, setPhoneError] = useState(false);
//   const [addModalVisible, setAddModalVisible] = useState(false);
//   const [newName, setNewName] = useState("");
//   const [newAddress, setNewAddress] = useState("");
//   const [newCity, setNewCity] = useState("");
//   const [newState, setNewState] = useState("");
//   const [editModalVisible, setEditModalVisible] = useState(false);
//   const [editName, setEditName] = useState("");
//   const [editAddress, setEditAddress] = useState("");
//   const [editCity, setEditCity] = useState("");
//   const [editState, setEditState] = useState("");
//   const [editShopName, setEditShopName] = useState("");
//   const [editCustomerType, setEditCustomerType] = useState("customer");
//   const [sameAsBuyer, setSameAsBuyer] = useState(true);
//   const [shipToName, setShipToName] = useState("");
//   const [shipToPhone, setShipToPhone] = useState("");
//   const [shipToAddress, setShipToAddress] = useState("");
//   const [shipToCity, setShipToCity] = useState("");
//   const [shipToState, setShipToState] = useState("");
//   const [courierCharge, setCourierCharge] = useState("0");
//   const [gstAmount, setGstAmount] = useState("0");
//   const [selectedSP, setSelectedSP] = useState(null);
//   const [dropdownOpen, setDropdownOpen] = useState(false);
//   const [discount, setDiscount] = useState("0");
//   const [confirmVisible, setConfirmVisible] = useState(false);
//   const [isConfirming, setIsConfirming] = useState(false);
//   const [selectedPaymentMode, setSelectedPaymentMode] = useState(
//     initialPaymentMode ? { label: initialPaymentMode } : null,
//   );
//   const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
//   const [customerType, setCustomerType] = useState("customer");
//   const [shopName, setShopName] = useState("");
//   const [orderType, setOrderType] = useState("");
//   const [orderTypeDropdownOpen, setOrderTypeDropdownOpen] = useState(false);
  
//   // ─── Add Salesperson Modal ─────────────────────────────────────────────
//   const [addSalespersonModalVisible, setAddSalespersonModalVisible] = useState(false);
//   const [newSalespersonName, setNewSalespersonName] = useState("");
//   const [salespersonAddErrorMsg, setSalespersonAddErrorMsg] = useState("");

//   // Refs
//   const paymentDropdownRef = useRef();
//   const salespersonDropdownRef = useRef();
//   const orderTypeDropdownRef = useRef();

//   // ─── Load salespersons on mount ────────────────────────────────────────
//   useEffect(() => {
//     dispatch(fetchSalespersons());
//   }, [dispatch]);

//   // ─── Reset salesperson error when modal closes ────────────────────────
//   useEffect(() => {
//     if (!addSalespersonModalVisible) {
//       setSalespersonAddErrorMsg("");
//       dispatch(clearSalespersonErrors());
//     }
//   }, [addSalespersonModalVisible, dispatch]);

//   useEffect(() => {
//     if (salespersonAddError) {
//       setSalespersonAddErrorMsg(salespersonAddError);
//     }
//   }, [salespersonAddError]);

//   // ─── Customer effects ──────────────────────────────────────────────────
//   useEffect(() => {
//     if (!cartItems) navigate("/order-cart");
//   }, [cartItems, navigate]);

//   useEffect(() => {
//     return () => dispatch(resetCustomer());
//   }, [dispatch]);

//   useEffect(() => {
//     if (buyerPhone.length === 10) {
//       setPhoneError(false);
//       dispatch(lookupCustomer(buyerPhone));
//     } else {
//       dispatch(resetCustomer());
//     }
//   }, [buyerPhone, dispatch]);

//   useEffect(() => {
//     if (addSuccess) {
//       setAddModalVisible(false);
//       setNewName("");
//       setNewAddress("");
//       setNewCity("");
//       setNewState("");
//       dispatch(clearAddState());
//       dispatch(lookupCustomer(buyerPhone));
//     }
//     if (customerError && addLoading === false) {
//       alert("Error: " + customerError);
//     }
//   }, [addSuccess, customerError, addLoading, dispatch, buyerPhone]);

//   useEffect(() => {
//     if (updateSuccess) {
//       setEditModalVisible(false);
//       dispatch(lookupCustomer(buyerPhone));
//       dispatch(clearUpdateState());
//       alert("Customer details updated successfully.");
//     }
//     if (customerError && updateLoading === false && updateSuccess === false) {
//       alert("Error: " + customerError);
//     }
//   }, [updateSuccess, customerError, updateLoading, dispatch, buyerPhone]);

//   // ─── Close dropdowns ──────────────────────────────────────────────────
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         paymentDropdownRef.current &&
//         !paymentDropdownRef.current.contains(event.target)
//       ) {
//         setPaymentDropdownOpen(false);
//       }
//       if (
//         salespersonDropdownRef.current &&
//         !salespersonDropdownRef.current.contains(event.target)
//       ) {
//         setDropdownOpen(false);
//       }
//       if (
//         orderTypeDropdownRef.current &&
//         !orderTypeDropdownRef.current.contains(event.target)
//       ) {
//         setOrderTypeDropdownOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ─── Add Salesperson Handler ──────────────────────────────────────────
//   const handleAddSalesperson = async () => {
//     const trimmedName = newSalespersonName.trim();
//     if (!trimmedName) {
//       setSalespersonAddErrorMsg("Please enter a salesperson name.");
//       return;
//     }

//     setSalespersonAddErrorMsg("");

//     try {
//       const result = await dispatch(addSalesperson(trimmedName)).unwrap();
      
//       setSelectedSP(result);
//       setNewSalespersonName("");
//       setAddSalespersonModalVisible(false);
//       setDropdownOpen(false);
      
//       await dispatch(fetchSalespersons());
//     } catch (err) {
//       setSalespersonAddErrorMsg(err || "Failed to add salesperson. Please try again.");
//     }
//   };

//   // ─── Get batch allocations ────────────────────────────────────────────
//   const getBatchAllocationsForItem = useCallback(
//     (item) => {
//       let allocations = [];

//       if (batchSelections && batchSelections[item.id]?.batchAllocations) {
//         allocations = batchSelections[item.id].batchAllocations;
//       }

//       if (allocations.length === 0 && item.batchAllocations) {
//         allocations = item.batchAllocations;
//       }

//       return allocations;
//     },
//     [batchSelections],
//   );

//   // ─── goToInvoice ──────────────────────────────────────────────────────
//   const goToInvoice = async () => {
//     if (!selectedSP) {
//       alert("Please select a Salesperson before proceeding.");
//       setConfirmVisible(false);
//       return;
//     }

//     setIsConfirming(true);
//     setConfirmVisible(false);

//     try {
//       const finalShipToName = sameAsBuyer
//         ? customer?.type === "shop"
//           ? customer?.name
//           : customer?.name
//         : shipToName;
//       const finalShipToPhone = sameAsBuyer ? buyerPhone : shipToPhone;
//       const finalShipToAddress = sameAsBuyer
//         ? customer?.address || ""
//         : shipToAddress;
//       const finalShipToCity = sameAsBuyer ? customer?.city || "" : shipToCity;
//       const finalShipToState = sameAsBuyer
//         ? customer?.state || ""
//         : shipToState;

//       const invoiceItems = cartItems.map((item) => {
//         const allocations = getBatchAllocationsForItem(item);
//         const hasValidBatches = allocations && allocations.length > 0 && 
//           allocations.some(a => a.batchNumber && a.batchNumber !== 'default');

//         const baseItem = {
//           productId: item.id,
//           name: item.name,
//           qty: item.qty || 0,
//           price: item.price || item.originalPrice || 0,
//           useDefaultPrice: !hasValidBatches,
//         };

//         if (hasValidBatches) {
//           baseItem.batchAllocations = allocations
//             .filter(a => a.batchNumber && a.batchNumber !== 'default')
//             .map((alloc) => ({
//               batchNumber: alloc.batchNumber,
//               qty: alloc.qty || item.qty || 0,
//               purchaseCost: alloc.purchaseCost || item.price || 0,
//               sellingPrice: alloc.sellingPrice || item.price || 0,
//             }));
//         }

//         return baseItem;
//       });

//       const subtotal = cartItems.reduce(
//         (sum, item) =>
//           sum + (item.price || item.originalPrice || 0) * (item.qty || 0),
//         0,
//       );
//       const discountAmount = parseFloat(discount) || 0;
//       const afterDiscount = Math.max(subtotal - discountAmount, 0);
//       const courier = parseFloat(courierCharge) || 0;
//       const gst = parseFloat(gstAmount) || 0;
//       const totalWithCourier = afterDiscount + courier + gst;

//       const payload = {
//         billerName: user?.name || "Unknown",
//         items: invoiceItems,
//         totalAmount: totalWithCourier,
//         paymentMode: selectedPaymentMode?.label || initialPaymentMode || "Cash",
//         status: "completed",
//         customerPhone: buyerPhone,
//         customerName: customer?.name || "Guest",
//         customerType: customer?.type || "customer",
//         shopName: customer?.shopName || "",
//         customerAddress: customer?.address || "",
//         customerCity: customer?.city || "",
//         customerState: customer?.state || "",
//         sameAsBuyer,
//         shippingAddress: {
//           name: finalShipToName || customer?.name || "Guest",
//           phone: finalShipToPhone || buyerPhone || "",
//           address: finalShipToAddress || customer?.address || "",
//           city: finalShipToCity || customer?.city || "",
//           state: finalShipToState || customer?.state || "",
//         },
//         subtotal,
//         discount: discountAmount,
//         courierCharge: courier,
//         gstAmount: gst,
//         salesperson: selectedSP?.name || "",
//         referenceNo: referenceNo || "",
//         invoiceDate: date || new Date().toISOString(),
//         orderType: orderType || "",
//         priceType: priceType || "retailerPrice",
//         allowDefaultBatches: true,
//         hasDefaultBatches:
//           hasDefaultBatches ||
//           invoiceItems.some((item) => item.useDefaultPrice),
//       };

//       const invoiceRes = await API.post("/api/invoices", payload);
//       const invoiceNumber = invoiceRes.data.invoice.invoiceNumber;

//       await dispatch(fetchProducts());

//       navigate("/invoice", {
//         state: {
//           invoiceNumber,
//           items: cartItems,
//           total: totalWithCourier,
//           paymentMode: selectedPaymentMode?.label || initialPaymentMode,
//           date: date,
//           buyerName: customer?.name || "—",
//           buyerPhone,
//           buyerAddress: customer?.address || "",
//           buyerCity: customer?.city || "",
//           buyerState: customer?.state || "",
//           courierCharge: courier,
//           discount: discountAmount,
//           gstAmount: gst,
//           salesperson: selectedSP?.name || "",
//           referenceNo,
//           shipToName: finalShipToName,
//           shipToPhone: finalShipToPhone,
//           shipToAddress: finalShipToAddress,
//           shipToCity: finalShipToCity,
//           shipToState: finalShipToState,
//           customerType: customer?.type,
//           shopName: customer?.shopName,
//           orderType: orderType || "",
//           batchSelections: batchSelections,
//           showBatchSelector: showBatchSelector,
//           priceType: priceType,
//         },
//       });
//     } catch (err) {
//       console.error("❌ Invoice creation error:", err);

//       let errorMessage = "Failed to confirm order. Please try again.";

//       if (err?.response?.data?.message) {
//         errorMessage = err.response.data.message;
//         if (err.response.data.code === "INSUFFICIENT_STOCK") {
//           errorMessage = `Insufficient stock: ${err.response.data.message}`;
//         }
//       } else if (err?.message) {
//         errorMessage = err.message;
//       }

//       alert(errorMessage);
//     } finally {
//       setIsConfirming(false);
//     }
//   };

//   // ─── Handlers ────────────────────────────────────────────────────────────
//   const handleViewInvoice = () => {
//     if (!selectedSP) {
//       alert("Please select a Salesperson before proceeding.");
//       return;
//     }

//     if (!referenceNo.trim()) {
//       alert("Please enter a Reference Number before proceeding.");
//       return;
//     }

//     if (!orderType) {
//       alert("Please select an Order Type before proceeding.");
//       return;
//     }

//     if (buyerPhone.length < 10) {
//       setPhoneError(true);
//       alert("Please enter a valid 10-digit phone number.");
//       return;
//     }
//     if (lookupState === "loading") {
//       alert("Verifying customer details...");
//       return;
//     }
//     if (lookupState === "notfound") {
//       setPhoneError(true);
//       if (
//         window.confirm(
//           "Customer not found. Would you like to add this customer?",
//         )
//       ) {
//         setAddModalVisible(true);
//       }
//       return;
//     }
//     if (lookupState === "error") {
//       alert("Could not reach server. Check your connection.");
//       return;
//     }
//     if (lookupState === "found" && customer) {
//       setPhoneError(false);
//       setConfirmVisible(true);
//     }
//   };

//   const handleSaveNewCustomer = () => {
//     if (!newName.trim()) {
//       alert("Customer name is required.");
//       return;
//     }
//     if (customerType === "shop" && !shopName.trim()) {
//       alert("Shop name is required.");
//       return;
//     }
//     dispatch(
//       addCustomer({
//         phone: buyerPhone,
//         name: newName.trim(),
//         address: newAddress.trim(),
//         city: newCity.trim(),
//         state: newState.trim(),
//         type: customerType,
//         shopName: shopName.trim(),
//       }),
//     );
//   };

//   const discountAmount = parseFloat(discount) || 0;
//   const subtotal = cartItems
//     ? cartItems.reduce(
//         (sum, item) =>
//           sum + (item.price || item.originalPrice || 0) * (item.qty || 0),
//         0,
//       )
//     : 0;
//   const afterDiscount = Math.max(subtotal - discountAmount, 0);
//   const courier = parseFloat(courierCharge) || 0;
//   const gst = parseFloat(gstAmount) || 0;
//   const grandTotalWithCourier = afterDiscount + courier + gst;

//   if (!cartItems)
//     return <div className="loading-state">No cart items found</div>;

//   return (
//     <div className={`order-success-page ${isDark ? "dark" : ""}`}>
//       <div className="order-success-container">
//         <div className="success-header">📄 Order Confirm</div>

//         {/* Batch Summary */}
//         {showBatchSelector && (
//           <div className="batch-summary-card">
//             <h4>Batch Allocations</h4>
//             {cartItems.map((item) => {
//               const allocations = getBatchAllocationsForItem(item);
//               const hasValidBatches = allocations && allocations.length > 0 && 
//                 allocations.some(a => a.batchNumber && a.batchNumber !== 'default');

//               return (
//                 <div key={item.id} className="batch-summary-item">
//                   <div className="batch-summary-product">{item.name}</div>
//                   <div className="batch-summary-batches">
//                     {hasValidBatches ? (
//                       allocations
//                         .filter(a => a.batchNumber && a.batchNumber !== 'default')
//                         .map((alloc, idx) => (
//                           <span key={idx} className="batch-summary-tag">
//                             {alloc.batchNumber}: {alloc.qty || item.qty} units
//                           </span>
//                         ))
//                     ) : (
//                       <span className="batch-summary-tag default-batch">
//                         Using default price (no batch)
//                       </span>
//                     )}
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}

//         <div className="form-card">
//           <h3>Delivery & Invoice Details</h3>

//           {/* Reference No */}
//           <div className="field-group">
//             <label>
//               <FileText size={14} /> Reference No. *
//             </label>
//             <input
//               type="text"
//               value={referenceNo}
//               onChange={(e) => setReferenceNo(e.target.value)}
//               placeholder="e.g. PO-12345 (Required)"
//               className="field-input"
//               required
//             />
//             {!referenceNo.trim() && (
//               <div className="error-text">Reference Number is required</div>
//             )}
//           </div>

//           {/* Order Type */}
//           <div className="field-group" ref={orderTypeDropdownRef}>
//             <label>
//               <FileText size={14} /> Order Type *
//             </label>
//             <div
//               className="custom-select"
//               onClick={() => setOrderTypeDropdownOpen(!orderTypeDropdownOpen)}
//             >
//               <span>{orderType ? orderType : "Select order type…"}</span>
//               <ChevronDown
//                 size={16}
//                 className={`dropdown-arrow ${orderTypeDropdownOpen ? "open" : ""}`}
//               />
//             </div>
//             {orderTypeDropdownOpen && (
//               <div className="dropdown-list">
//                 {ORDER_TYPES.map((type) => (
//                   <div
//                     key={type.id}
//                     className={`dropdown-item ${orderType === type.label ? "active" : ""}`}
//                     onClick={() => {
//                       setOrderType(type.label);
//                       setOrderTypeDropdownOpen(false);
//                     }}
//                   >
//                     {type.label}
//                   </div>
//                 ))}
//               </div>
//             )}
//             {!orderType && (
//               <div className="error-text">Order Type is required</div>
//             )}
//           </div>

//           {/* Payment Mode */}
//           <div className="field-group" ref={paymentDropdownRef}>
//             <label>
//               <CreditCard size={14} /> Payment Mode
//             </label>
//             <div
//               className="custom-select"
//               onClick={() => setPaymentDropdownOpen(!paymentDropdownOpen)}
//             >
//               <span>
//                 {selectedPaymentMode
//                   ? selectedPaymentMode.label
//                   : "Select payment mode…"}
//               </span>
//               <ChevronDown
//                 size={16}
//                 className={`dropdown-arrow ${paymentDropdownOpen ? "open" : ""}`}
//               />
//             </div>
//             {paymentDropdownOpen && (
//               <div className="dropdown-list">
//                 {PAYMENT_MODES.map((mode) => (
//                   <div
//                     key={mode.id}
//                     className={`dropdown-item ${selectedPaymentMode?.id === mode.id ? "active" : ""}`}
//                     onClick={() => {
//                       setSelectedPaymentMode(mode);
//                       setPaymentDropdownOpen(false);
//                     }}
//                   >
//                     {mode.label}
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* Subtotal */}
//           <div className="field-group">
//             <label>
//               <IndianRupee size={14} /> Subtotal
//             </label>
//             <div className="readonly-field">
//               ₹{subtotal.toLocaleString("en-IN")}
//             </div>
//           </div>

//           {/* Discount */}
//           <div className="field-group">
//             <label>
//               <IndianRupee size={14} /> Discount
//             </label>
//             <input
//               type="number"
//               value={discount}
//               onChange={(e) => setDiscount(e.target.value)}
//               className="field-input"
//               placeholder="0"
//             />
//           </div>

//           {/* Invoice Date */}
//           <div className="field-group">
//             <label>
//               <Calendar size={14} /> Invoice Date
//             </label>
//             <div className="readonly-field">
//               {formatDate(date || new Date().toISOString())}
//             </div>
//           </div>

//           {/* ─── Salesperson with "Add New" Button at Top ─── */}
//           <div className="field-group" ref={salespersonDropdownRef}>
//             <div style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               marginBottom: "4px",
//             }}>
//               <label style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "0.5rem",
//                 fontSize: "0.85rem",
//                 fontWeight: 500,
//                 color: "var(--text-secondary)",
//                 margin: 0,
//               }}>
//                 <User size={14} /> Salesperson *
//               </label>
//               <button
//                 type="button"
//                 onClick={() => setAddSalespersonModalVisible(true)}
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "4px",
//                   background: "var(--red)",
//                   color: "#fff",
//                   border: "none",
//                   padding: "4px 12px",
//                   borderRadius: "4px",
//                   fontSize: "0.7rem",
//                   fontWeight: 600,
//                   cursor: "pointer",
//                   transition: "all 0.2s",
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.background = "var(--red-bright)";
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.background = "var(--red)";
//                 }}
//               >
//                 <Plus size={14} /> Add New
//               </button>
//             </div>

//             <div
//               className={`custom-select ${!selectedSP ? "error" : ""}`}
//               onClick={() => !salespersonsLoading && setDropdownOpen(!dropdownOpen)}
//               style={{
//                 borderColor: !selectedSP ? "var(--red-bright)" : undefined,
//                 cursor: salespersonsLoading ? "default" : "pointer",
//               }}
//             >
//               <span style={{ 
//                 color: !selectedSP ? "var(--text-muted)" : undefined 
//               }}>
//                 {salespersonsLoading ? (
//                   <Loader size={16} className="spin" style={{ display: "inline-block", marginRight: "6px" }} />
//                 ) : null}
//                 {selectedSP ? selectedSP.name : "Select salesperson… (Required)"}
//               </span>
//               <ChevronDown
//                 size={16}
//                 className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
//               />
//             </div>
//             {!selectedSP && !salespersonsLoading && (
//               <div className="error-text">Salesperson is required</div>
//             )}
//             {salespersonsLoading && (
//               <div className="status-row">Loading salespersons...</div>
//             )}
//             {dropdownOpen && !salespersonsLoading && (
//               <div className="dropdown-list" style={{ maxHeight: "200px", overflowY: "auto" }}>
//                 {salespersons.length === 0 ? (
//                   <div style={{ padding: "10px 14px", fontSize: "13px", color: "var(--text-muted)" }}>
//                     No salespersons found. Click "Add New" to create one.
//                   </div>
//                 ) : (
//                   salespersons.map((sp) => (
//                     <div
//                       key={sp.id}
//                       className={`dropdown-item ${selectedSP?.id === sp.id ? "active" : ""}`}
//                       onClick={() => {
//                         setSelectedSP(sp);
//                         setDropdownOpen(false);
//                       }}
//                     >
//                       {sp.name}
//                     </div>
//                   ))
//                 )}
//               </div>
//             )}
//           </div>

//           {/* Phone & Customer Lookup */}
//           <div className="field-group">
//             <label>
//               <Phone size={14} /> Phone Number *
//             </label>
//             <input
//               type="tel"
//               maxLength="10"
//               value={buyerPhone}
//               onChange={(e) => setBuyerPhone(e.target.value)}
//               className={`field-input ${phoneError ? "error" : ""}`}
//               placeholder="Enter 10-digit mobile number"
//             />
//             {phoneError && buyerPhone.length < 10 && (
//               <div className="error-text">
//                 Enter a valid 10-digit phone number
//               </div>
//             )}
//             {lookupState === "loading" && (
//               <div className="status-row">Checking customer database...</div>
//             )}
//             {lookupState === "found" && customer && (
//               <div className="found-card">
//                 <div className="found-header">
//                   <CheckCircle size={15} /> Customer Found
//                   <button
//                     className="edit-btn"
//                     onClick={() => {
//                       setEditName(customer.name || "");
//                       setEditAddress(customer.address || "");
//                       setEditCity(customer.city || "");
//                       setEditState(customer.state || "");
//                       setEditShopName(customer.shopName || "");
//                       setEditCustomerType(customer.type || "customer");
//                       setEditModalVisible(true);
//                     }}
//                   >
//                     Edit
//                   </button>
//                 </div>
//                 <div className="customer-name">
//                   {customer.type === "shop"
//                     ? `${customer.name} (${customer.shopName})`
//                     : customer.name}
//                 </div>
//                 {customer.address && (
//                   <div className="customer-sub address-line">
//                     <MapPin size={14} />
//                     <span>{customer.address}</span>
//                   </div>
//                 )}
//                 {(customer.city || customer.state) && (
//                   <div className="customer-sub">
//                     <Building2 size={14} />{" "}
//                     {[customer.city, customer.state].filter(Boolean).join(", ")}
//                   </div>
//                 )}
//               </div>
//             )}
//             {lookupState === "notfound" && (
//               <div className="notfound-box">
//                 <AlertTriangle size={13} /> No customer found.
//                 <button
//                   className="add-btn"
//                   onClick={() => setAddModalVisible(true)}
//                 >
//                   + Add Customer
//                 </button>
//               </div>
//             )}
//             {lookupState === "error" && (
//               <div className="notfound-box">
//                 <WifiOff size={13} /> Could not reach server.
//                 <button
//                   className="add-btn"
//                   onClick={() => dispatch(lookupCustomer(buyerPhone))}
//                 >
//                   Retry
//                 </button>
//               </div>
//             )}
//           </div>

//           {/* Shipping Address */}
//           <div className="field-group">
//             <label>
//               <Truck size={14} /> Shipping Address
//             </label>
//             <div className="switch-row">
//               <span>Same as buyer address</span>
//               <button
//                 className={`switch ${sameAsBuyer ? "active" : ""}`}
//                 onClick={() => setSameAsBuyer(!sameAsBuyer)}
//               >
//                 {sameAsBuyer ? "ON" : "OFF"}
//               </button>
//             </div>
//             {!sameAsBuyer && (
//               <>
//                 <input
//                   type="text"
//                   placeholder="Ship to Name"
//                   value={shipToName}
//                   onChange={(e) => setShipToName(e.target.value)}
//                   className="field-input"
//                 />
//                 <input
//                   type="tel"
//                   placeholder="Ship to Phone"
//                   value={shipToPhone}
//                   onChange={(e) => setShipToPhone(e.target.value)}
//                   className="field-input"
//                 />
//                 <textarea
//                   placeholder="Ship to Address"
//                   value={shipToAddress}
//                   onChange={(e) => setShipToAddress(e.target.value)}
//                   rows={2}
//                   className="field-input"
//                 />
//                 <div className="row-inputs">
//                   <input
//                     type="text"
//                     placeholder="City"
//                     value={shipToCity}
//                     onChange={(e) => setShipToCity(e.target.value)}
//                     className="field-input"
//                   />
//                   <input
//                     type="text"
//                     placeholder="State"
//                     value={shipToState}
//                     onChange={(e) => setShipToState(e.target.value)}
//                     className="field-input"
//                   />
//                 </div>
//               </>
//             )}
//           </div>

//           {/* GST Amount */}
//           <div className="field-group">
//             <label>
//               <IndianRupee size={14} /> GST (₹)
//             </label>
//             <input
//               type="text"
//               value={gstAmount}
//               onChange={(e) => setGstAmount(e.target.value)}
//               className="field-input"
//               placeholder="0"
//             />
//           </div>

//           {/* Courier Charge */}
//           <div className="field-group">
//             <label>
//               <Truck size={14} /> Courier Charge (₹)
//             </label>
//             <input
//               type="text"
//               value={courierCharge}
//               onChange={(e) => setCourierCharge(e.target.value)}
//               className="field-input"
//               placeholder="0"
//             />
//           </div>

//           {/* Total Preview */}
//           <div className="total-preview">
//             <div className="summary-row">
//               <span>Subtotal</span>
//               <span>₹{subtotal.toLocaleString("en-IN")}</span>
//             </div>
//             {discountAmount > 0 && (
//               <div className="summary-row">
//                 <span>Discount</span>
//                 <span className="discount">
//                   - ₹{discountAmount.toLocaleString("en-IN")}
//                 </span>
//               </div>
//             )}
//             <div className="summary-row">
//               <span>Courier Charge</span>
//               <span>₹{courier.toLocaleString("en-IN")}</span>
//             </div>
//             {gst > 0 && (
//               <div className="summary-row">
//                 <span>GST</span>
//                 <span>₹{gst.toLocaleString("en-IN")}</span>
//               </div>
//             )}
//             <hr />
//             <div className="summary-row total">
//               <span>Grand Total</span>
//               <span>₹{grandTotalWithCourier.toLocaleString("en-IN")}</span>
//             </div>
//           </div>
//         </div>

//         <button
//           className="primary-btn"
//           onClick={handleViewInvoice}
//           disabled={isConfirming || !referenceNo.trim() || !orderType || !selectedSP || salespersonsLoading}
//           style={{
//             opacity: !referenceNo.trim() || !orderType || !selectedSP || salespersonsLoading ? "0.5" : "1",
//           }}
//         >
//           <FileText size={18} /> View Invoice
//         </button>
//       </div>

//       {/* ─── Add Salesperson Modal ─── */}
//       {addSalespersonModalVisible && (
//         <div
//           className="modal-overlay"
//           onClick={() => {
//             if (!addingSalesperson) {
//               setAddSalespersonModalVisible(false);
//               setSalespersonAddErrorMsg("");
//             }
//           }}
//         >
//           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//             <div className="modal-header">
//               <UserPlus size={18} /> Add New Salesperson
//               <button
//                 className="close-btn"
//                 onClick={() => {
//                   if (!addingSalesperson) {
//                     setAddSalespersonModalVisible(false);
//                     setSalespersonAddErrorMsg("");
//                   }
//                 }}
//                 disabled={addingSalesperson}
//               >
//                 <X size={18} />
//               </button>
//             </div>
//             <div className="modal-body">
//               <div className="phone-pill" style={{ background: "var(--bg-surface)" }}>
//                 <User size={13} /> New Salesperson
//               </div>
//               <input
//                 type="text"
//                 placeholder="Enter salesperson name *"
//                 value={newSalespersonName}
//                 onChange={(e) => {
//                   setNewSalespersonName(e.target.value);
//                   if (salespersonAddErrorMsg) setSalespersonAddErrorMsg("");
//                 }}
//                 className="modal-input"
//                 onKeyDown={(e) => {
//                   if (e.key === "Enter" && !addingSalesperson) {
//                     handleAddSalesperson();
//                   }
//                 }}
//                 autoFocus
//                 disabled={addingSalesperson}
//               />
//               {salespersonAddErrorMsg && (
//                 <div style={{ 
//                   fontSize: "0.75rem", 
//                   color: "var(--red-bright)", 
//                   marginBottom: "0.75rem",
//                   fontWeight: 500,
//                 }}>
//                   ⚠️ {salespersonAddErrorMsg}
//                 </div>
//               )}
//               <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
//                 This will add the salesperson to the database for all future invoices.
//               </div>
//               <button
//                 className="save-btn"
//                 onClick={handleAddSalesperson}
//                 disabled={addingSalesperson}
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   gap: "6px",
//                 }}
//               >
//                 {addingSalesperson ? (
//                   <>
//                     <Loader size={16} className="spin" /> Adding...
//                   </>
//                 ) : (
//                   <>
//                     <Plus size={16} /> Add Salesperson
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Add Customer Modal */}
//       {addModalVisible && (
//         <div
//           className="modal-overlay"
//           onClick={() => setAddModalVisible(false)}
//         >
//           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//             <div className="modal-header">
//               <UserPlus size={18} /> Add New Customer
//               <button
//                 className="close-btn"
//                 onClick={() => setAddModalVisible(false)}
//               >
//                 <X size={18} />
//               </button>
//             </div>
//             <div className="modal-body">
//               <div className="phone-pill">
//                 <Phone size={13} /> {buyerPhone}
//               </div>
//               <div className="row-buttons">
//                 <button
//                   className={`type-btn ${customerType === "customer" ? "active" : ""}`}
//                   onClick={() => setCustomerType("customer")}
//                 >
//                   Customer
//                 </button>
//                 <button
//                   className={`type-btn ${customerType === "shop" ? "active" : ""}`}
//                   onClick={() => setCustomerType("shop")}
//                 >
//                   Shop
//                 </button>
//               </div>
//               <input
//                 type="text"
//                 placeholder="Customer Name *"
//                 value={newName}
//                 onChange={(e) => setNewName(e.target.value)}
//                 className="modal-input"
//               />
//               {customerType === "shop" && (
//                 <input
//                   type="text"
//                   placeholder="Shop Name *"
//                   value={shopName}
//                   onChange={(e) => setShopName(e.target.value)}
//                   className="modal-input"
//                 />
//               )}
//               <label className="field-label">Delivery Address</label>
//               <textarea
//                 placeholder="Street, landmark, area…"
//                 value={newAddress}
//                 onChange={(e) => setNewAddress(e.target.value)}
//                 rows={3}
//                 className="modal-input address-textarea"
//               />
//               <div className="row-inputs">
//                 <input
//                   type="text"
//                   placeholder="City"
//                   value={newCity}
//                   onChange={(e) => setNewCity(e.target.value)}
//                   className="modal-input"
//                 />
//                 <input
//                   type="text"
//                   placeholder="State"
//                   value={newState}
//                   onChange={(e) => setNewState(e.target.value)}
//                   className="modal-input"
//                 />
//               </div>
//               <button
//                 className="save-btn"
//                 onClick={handleSaveNewCustomer}
//                 disabled={addLoading}
//               >
//                 {addLoading ? "Saving..." : "Save Customer"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Edit Customer Modal */}
//       {editModalVisible && (
//         <div
//           className="modal-overlay"
//           onClick={() => setEditModalVisible(false)}
//         >
//           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//             <div className="modal-header">
//               <UserPlus size={18} /> Edit Customer
//               <button
//                 className="close-btn"
//                 onClick={() => setEditModalVisible(false)}
//               >
//                 <X size={18} />
//               </button>
//             </div>
//             <div className="modal-body">
//               <div className="phone-pill">
//                 <Phone size={13} /> {buyerPhone}
//               </div>
//               <div className="row-buttons">
//                 <button
//                   className={`type-btn ${editCustomerType === "customer" ? "active" : ""}`}
//                   onClick={() => setEditCustomerType("customer")}
//                 >
//                   Customer
//                 </button>
//                 <button
//                   className={`type-btn ${editCustomerType === "shop" ? "active" : ""}`}
//                   onClick={() => setEditCustomerType("shop")}
//                 >
//                   Shop
//                 </button>
//               </div>
//               <input
//                 type="text"
//                 placeholder="Customer Name *"
//                 value={editName}
//                 onChange={(e) => setEditName(e.target.value)}
//                 className="modal-input"
//               />
//               {editCustomerType === "shop" && (
//                 <input
//                   type="text"
//                   placeholder="Shop Name *"
//                   value={editShopName}
//                   onChange={(e) => setEditShopName(e.target.value)}
//                   className="modal-input"
//                 />
//               )}
//               <label className="field-label">Delivery Address</label>
//               <textarea
//                 placeholder="Street, landmark, area…"
//                 value={editAddress}
//                 onChange={(e) => setEditAddress(e.target.value)}
//                 rows={3}
//                 className="modal-input address-textarea"
//               />
//               <div className="row-inputs">
//                 <input
//                   type="text"
//                   placeholder="City"
//                   value={editCity}
//                   onChange={(e) => setEditCity(e.target.value)}
//                   className="modal-input"
//                 />
//                 <input
//                   type="text"
//                   placeholder="State"
//                   value={editState}
//                   onChange={(e) => setEditState(e.target.value)}
//                   className="modal-input"
//                 />
//               </div>
//               <button
//                 className="save-btn"
//                 onClick={() => {
//                   if (!editName.trim()) return alert("Customer name required");
//                   dispatch(
//                     updateCustomer({
//                       phone: buyerPhone,
//                       data: {
//                         name: editName.trim(),
//                         address: editAddress.trim(),
//                         city: editCity.trim(),
//                         state: editState.trim(),
//                         type: editCustomerType,
//                         shopName: editShopName.trim(),
//                       },
//                     }),
//                   );
//                 }}
//                 disabled={updateLoading}
//               >
//                 {updateLoading ? "Saving..." : "Save Changes"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Confirm Modal */}
//       {confirmVisible && (
//         <div className="modal-overlay" onClick={() => setConfirmVisible(false)}>
//           <div
//             className="modal-content confirm-modal"
//             onClick={(e) => e.stopPropagation()}
//           >
//             <div className="modal-header">
//               <h3>
//                 <Check size={20} /> Ready to Generate Invoice?
//               </h3>
//               <button
//                 className="close-btn"
//                 onClick={() => setConfirmVisible(false)}
//               >
//                 <X size={18} />
//               </button>
//             </div>
//             <div className="modal-body">
//               <p>Please confirm the order details before proceeding.</p>

//               {showBatchSelector && (
//                 <div className="confirm-batch-summary">
//                   <h4>Batch Allocations</h4>
//                   {cartItems.map((item) => {
//                     const allocations = getBatchAllocationsForItem(item);
//                     const hasValidBatches = allocations && allocations.length > 0 && 
//                       allocations.some(a => a.batchNumber && a.batchNumber !== 'default');

//                     return (
//                       <div key={item.id} className="confirm-batch-item">
//                         <span className="confirm-batch-product">
//                           {item.name}
//                         </span>
//                         {hasValidBatches ? (
//                           allocations
//                             .filter(a => a.batchNumber && a.batchNumber !== 'default')
//                             .map((alloc, idx) => (
//                               <span key={idx} className="confirm-batch-tag">
//                                 {alloc.batchNumber}: {alloc.qty || item.qty}
//                               </span>
//                             ))
//                         ) : (
//                           <span className="confirm-batch-tag default-batch">
//                             Using default price (no batch)
//                           </span>
//                         )}
//                       </div>
//                     );
//                   })}
//                 </div>
//               )}

//               <div className="confirm-info">
//                 <div>
//                   <strong>Reference No:</strong> <span>{referenceNo}</span>
//                 </div>
//                 <div>
//                   <strong>Order Type:</strong> <span>{orderType || "—"}</span>
//                 </div>
//                 <div>
//                   <strong>Salesperson:</strong>{" "}
//                   <span>{selectedSP?.name || "—"}</span>
//                 </div>
//                 <div>
//                   <strong>Buyer:</strong>
//                   <span>
//                     {customer?.type === "shop"
//                       ? `${customer?.name} (${customer?.shopName})`
//                       : customer?.name || "—"}
//                   </span>
//                 </div>
//                 <div>
//                   <strong>Phone:</strong> <span>{buyerPhone}</span>
//                 </div>
//                 <div>
//                   <strong>Address:</strong>{" "}
//                   <span>{customer?.address || "—"}</span>
//                 </div>
//                 <div>
//                   <strong>City/State:</strong>{" "}
//                   <span>
//                     {[customer?.city, customer?.state]
//                       .filter(Boolean)
//                       .join(", ") || "—"}
//                   </span>
//                 </div>
//                 <div>
//                   <strong>Courier:</strong> <span>₹{courier}</span>
//                 </div>
//                 {discountAmount > 0 && (
//                   <div>
//                     <strong>Discount:</strong> <span>₹{discountAmount}</span>
//                   </div>
//                 )}
//                 {gst > 0 && (
//                   <div>
//                     <strong>GST:</strong> <span>₹{gst}</span>
//                   </div>
//                 )}
//                 <div>
//                   <strong>Grand Total:</strong>{" "}
//                   <span>₹{grandTotalWithCourier.toLocaleString("en-IN")}</span>
//                 </div>
//               </div>
//               <div className="confirm-buttons">
//                 <button
//                   className="primary-btn"
//                   onClick={goToInvoice}
//                   disabled={isConfirming || !referenceNo.trim() || !orderType || !selectedSP}
//                 >
//                   {isConfirming
//                     ? "Processing..."
//                     : "Confirm & Generate Invoice"}
//                 </button>
//                 <button
//                   className="outline-btn"
//                   onClick={() => setConfirmVisible(false)}
//                 >
//                   Go Back & Edit
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default OrderSuccessPage;